import { type FC, useEffect, useMemo } from 'react';
import { Route, type RouteComponentProps, Switch } from 'wouter';

import BannerGroupSliders from 'components/BannerSlider';
import CasinoCategories, { ItemType } from 'components/CasinoCategories';
import GamesList from 'components/GamesList';
import FavoritesGamesList from 'components/GamesList/FavoritesGamesList';
import LastWinners from 'components/LastWinners';
import Meta from 'components/Meta';
import SearchField from 'components/SearchField';
import SeoText from 'components/SeoText';
import { useConfig } from 'config';
import {
  CasinoGroup,
  type CasinoGroupPathParam,
  type CasinoSubpage,
  pathParamToCasinoGroupMap,
} from 'utils/casino';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import { useSearchQuery } from 'utils/location';
import { getAppProp } from 'utils/props';
import { Translation } from 'utils/translations';
import { AuthStatus, useClientProfile } from '../../store';

import styles from './CasinoPage.module.scss';
import useCasinoCategories from './useCasinoCategories';

export type CasinoPageParams = {
  group: CasinoGroupPathParam;
  subpage: CasinoSubpage;
};

const CasinoPage: FC<RouteComponentProps<CasinoPageParams>> = ({
  params: { group: groupParam },
}) => {
  const group = pathParamToCasinoGroupMap[groupParam];
  const configGroup =
    group === CasinoGroup.CASINO ? 'casinoBanner' : 'liveCasinoBanner';
  const config = useConfig(`config.siteConfig.casino.${configGroup}`);
  const lastWinners = useConfig('config.siteConfig.casino.lastWinners');
  const [query] = useSearchQuery();
  const search = query.get('q') ?? undefined;
  const { activeItems } = useCasinoCategories();
  const [{ status, data }] = useClientProfile();

  const categories = useMemo(
    () =>
      activeItems.reduce((acc, item) => {
        if (item.type === ItemType.category) {
          acc.push(item.id);
        }
        return acc;
      }, [] as string[]),
    [activeItems],
  );

  useEffect(() => {
    if (status === AuthStatus.resolving) return;

    pushToDataLayer(
      'casino_page_visit',
      {
        ...(data?.client && {
          client: {
            id: data.client.id,
          },
        }),
      },
      btag,
    );
  }, [data, status]);

  return (
    <div className={styles.wrapper}>
      {config?.group && (
        <BannerGroupSliders groupName={config.group} delay={config?.delay} />
      )}
      <SearchField placeholder="search_casino_games" />
      <CasinoCategories group={group} activeItems={activeItems} />

      <Switch>
        <Route path="/:group/favorites">
          <FavoritesGamesList slice={28} />
        </Route>
        <Route>
          <GamesList
            group={group}
            name={search}
            categories={categories}
            slice={28}
          />
        </Route>
      </Switch>

      {lastWinners?.enabled && (
        <LastWinners
          projectId={getAppProp('project_id')}
          delay={lastWinners.delay}
        />
      )}

      <Switch>
        <Route path="/:group/favorites">
          <Meta title={<Translation message="title_casino_favorites" />} />
        </Route>
        <Route>
          <SeoText />
        </Route>
      </Switch>
    </div>
  );
};

export default CasinoPage;
